import { LIBRARY_REQUIRED_FIELDS } from '@/features/metadata-library/config/validation';
import type { VenueFormValues } from '@/features/metadata-library/types/libraryFormValues';
import type { VenueSnapshot } from '@/features/metadata-library/types/libraryRequests';
import type {
  LibraryFieldsCatalog,
  VenueRecord,
} from '@/features/metadata-library/types/metadataLibrary';
import { nullableNumber, nullableText } from '@/shared/lib/inputValues';
import { omitNullFields } from '@/shared/lib/omitNullFields';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { catalogSchema } from './catalog';
import {
  coordinateInputSchema,
  enumInputSchema,
  integerInputSchema,
  textInputSchema,
} from './fieldSchema';
import { libraryFieldValueError } from './validate';
import {
  composeVenueAddress,
  venueAddressErrors,
  venueAddressFormat,
  venueAddressValues,
} from './venueAddress';

export function venueFormDefinition(catalog: LibraryFieldsCatalog) {
  const fields = catalog.fields;
  const requiredFields = LIBRARY_REQUIRED_FIELDS.environment;
  const addressSchema = catalogSchema(fields, 'address');
  const schema = z
    .object({
      businessName: textInputSchema(fields, 'businessName', {
        required: requiredFields.businessName,
      }),
      businessId: z.string(),
      environmentL1: enumInputSchema(fields, 'environmentL1', {
        required: requiredFields.environmentL1,
      }),
      environmentL2: enumInputSchema(fields, 'environmentL2', {
        required: requiredFields.environmentL2,
      }),
      latitude: coordinateInputSchema(fields, 'latitude', { required: requiredFields.latitude }),
      longitude: coordinateInputSchema(fields, 'longitude', {
        required: requiredFields.longitude,
      }),
      address: z.string(),
      addressCountry: z.string(),
      addressLine1: z.string(),
      subLocality: z.string(),
      locality: z.string(),
      adminArea: z.string(),
      employees: integerInputSchema(fields, 'employees', {
        required: requiredFields.employees,
      }),
    })
    .strict()
    .superRefine((values, context) => {
      const format = venueAddressFormat(values.addressCountry);
      if (!format) {
        if (requiredFields.addressCountry) {
          context.addIssue({
            code: 'custom',
            path: ['addressCountry'],
            message: t`Choose the venue's country.`,
          });
        }
        return;
      }

      for (const { key, message } of venueAddressErrors(values)) {
        context.addIssue({ code: 'custom', path: [key], message });
      }

      const composedAddress = composeVenueAddress(values);
      if (!composedAddress) return;

      if (values.address !== composedAddress) {
        context.addIssue({
          code: 'custom',
          path: ['address'],
          message: t`Adjust the address to match the selected country and address parts.`,
        });
      }

      const addressMessage = libraryFieldValueError(addressSchema, composedAddress, {
        required: true,
      });
      if (addressMessage)
        context.addIssue({ code: 'custom', path: ['address'], message: addressMessage });
    });
  return { fields, schema };
}

export function venueValuesFromRecord(record: VenueRecord): VenueFormValues {
  return {
    businessName: record.businessName ?? '',
    businessId: record.businessId ?? '',
    environmentL1: record.environmentL1 ?? '',
    environmentL2: record.environmentL2 ?? '',
    latitude: record.latitude === null ? '' : String(record.latitude),
    longitude: record.longitude === null ? '' : String(record.longitude),
    ...venueAddressValues(record.address),
    employees: record.employees === null ? '' : String(record.employees),
  };
}

export function venuePatchBody(values: VenueFormValues): VenueSnapshot {
  return {
    businessName: nullableText(values.businessName),
    businessId: nullableText(values.businessId),
    environmentL1: nullableText(values.environmentL1),
    environmentL2: nullableText(values.environmentL2),
    latitude: nullableNumber(values.latitude),
    longitude: nullableNumber(values.longitude),
    address: nullableText(composeVenueAddress(values) ?? ''),
    employees: nullableNumber(values.employees),
  };
}

export function venueCreateBody(values: VenueFormValues): Partial<VenueSnapshot> {
  return omitNullFields(venuePatchBody(values));
}

/** Returns displayable coordinates only once both raw form inputs are finite numbers. */
export function venueCoordinatesFromInputs(latitude: string, longitude: string) {
  if (!latitude.trim() || !longitude.trim()) return null;

  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);
  if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) return null;

  return { latitude: parsedLatitude, longitude: parsedLongitude };
}
